<script setup>
/* global axios */
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PromptVersionHistory from './PromptVersionHistory.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  agentId: { type: String, default: null },
  // O que aparece no canto do editor. Era o `agentId` cru — quem abre o prompt da Eva via
  // "77", que não diz nada. Continua caindo no id quando o nome não veio.
  agentName: { type: String, default: null },
  temperature: { type: Number, default: 0.7 },
  apiBasePath: { type: String, required: true },
  i18nPrefix: { type: String, default: 'POWER_AGENTS.FORM' },
  accountId: { type: [String, Number], default: null },
  inline: { type: Boolean, default: false },
});
const emit = defineEmits([
  'update:modelValue',
  'close',
  'version-restored',
  'expand',
]);
const { t } = useI18n();
const route = useRoute();
function tp(suffix, params) {
  return t(`${props.i18nPrefix}.${suffix}`, params);
}

// ── State ─────────────────────────────────────────────────────
const textareaRef = ref(null);
const showVersions = ref(false);
const savingVersion = ref(false);
const showSaveDialog = ref(false);
const versionDescription = ref('');
const savedIndicator = ref(true); // starts true (loaded from server)
const previewMode = ref(false);

// ── Computed ──────────────────────────────────────────────────
const localText = computed({
  get: () => props.modelValue,
  set: val => {
    savedIndicator.value = false;
    emit('update:modelValue', val);
  },
});

// Caracteres, não palavras: o que o modelo cobra e o que decide se o prefixo cruza o mínimo
// de cache é tamanho em token, e caractere é a aproximação útil disso. Contar palavra não
// ajuda ninguém a decidir nada.
const charCount = computed(() => (localText.value || '').length);

const lineCount = computed(() => {
  return (localText.value || '').split('\n').length;
});

// ── Methods ───────────────────────────────────────────────────
function close() {
  emit('close');
}

function handleKeydown(e) {
  // Escape closes the modal only — the inline instance stays mounted on the page,
  // so a page-wide Escape must not fire its (unheard) close.
  if (
    e.key === 'Escape' &&
    !props.inline &&
    !showSaveDialog.value &&
    !showVersions.value
  ) {
    close();
  }
  // Tab → 2 spaces, but only when THIS editor's textarea has focus. The listener
  // is document-level and the inline instance lives on a normal settings page —
  // without the focus check it would hijack Tab for the whole page (and both
  // instances would double-insert when the fullscreen editor is open).
  if (e.key === 'Tab') {
    const ta = textareaRef.value;
    if (!ta || document.activeElement !== ta) return;
    e.preventDefault();
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const val = ta.value;
    ta.value = val.substring(0, start) + '  ' + val.substring(end);
    ta.selectionStart = ta.selectionEnd = start + 2;
    // trigger update
    emit('update:modelValue', ta.value);
    savedIndicator.value = false;
  }
}

async function saveVersion() {
  if (!versionDescription.value.trim()) return;

  savingVersion.value = true;
  try {
    await axios.post(`${props.apiBasePath}/prompt-versions`, {
      description: versionDescription.value.trim(),
      system_prompt: localText.value,
      temperature: props.temperature,
    });
    useAlert(tp('EDITOR_VERSION_SAVED'));
    showSaveDialog.value = false;
    versionDescription.value = '';
  } catch (e) {
    useAlert(tp('EDITOR_VERSION_ERROR'));
  } finally {
    savingVersion.value = false;
  }
}

function onVersionRestored(version) {
  emit('version-restored', version);
  savedIndicator.value = true;
}

// ── Markdown rendering ───────────────────────────────────────
// Um bloco de tabela é: linha de cabeçalho, linha separadora (`|---|---|`) e as linhas de dados,
// todas começando com `|`. Qualquer coisa fora desse formato fica como estava — um prompt com um
// `|` solto no meio de uma frase não vira tabela quebrada.
const LINHA_SEPARADORA = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;

function celulas(linha) {
  return linha
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map(c => c.trim());
}

// Lê uma tabela a partir de `inicio` (cabeçalho + separador + dados), empurra o HTML em `saida` e
// devolve o índice da primeira linha que já não é da tabela.
function consumirTabela(linhas, inicio, saida) {
  const cabecalho = celulas(linhas[inicio]);
  let i = inicio + 2; // pula o cabeçalho e o separador
  const corpo = [];
  while (i < linhas.length && linhas[i].trim().startsWith('|')) {
    corpo.push(celulas(linhas[i]));
    i += 1;
  }

  const th = cabecalho.map(c => `<th class="md-th">${c}</th>`).join('');
  const tr = corpo
    .map(cols => {
      const tds = cols.map(c => `<td class="md-td">${c}</td>`).join('');
      return `<tr>${tds}</tr>`;
    })
    .join('');
  saida.push(
    `<table class="md-table"><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`
  );
  return i;
}

function renderTables(texto) {
  const linhas = texto.split('\n');
  const saida = [];
  let i = 0;

  while (i < linhas.length) {
    const ehTabela =
      linhas[i].trim().startsWith('|') &&
      i + 1 < linhas.length &&
      LINHA_SEPARADORA.test(linhas[i + 1]);

    if (ehTabela) {
      i = consumirTabela(linhas, i, saida);
    } else {
      saida.push(linhas[i]);
      i += 1;
    }
  }

  return saida.join('\n');
}

function renderMarkdown(text) {
  if (!text) return '';
  let html = text
    // Escape HTML first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.+)$/gm, '<h4 class="md-h4">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="md-h3">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="md-h2">$1</h2>')
    // Bold and italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="md-code">$1</code>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li class="md-li">$1</li>');

  // Tabelas. Vêm DEPOIS dos transformadores em linha (para negrito e código valerem dentro da
  // célula) e ANTES da quebra de linha, que destruiria as linhas da tabela.
  html = renderTables(html);

  html = html
    // Line breaks
    .replace(/\n\n/g, '</p><p class="md-p">')
    .replace(/\n/g, '<br>');
  return '<p class="md-p">' + html + '</p>';
}

const markdownHtml = computed(() => renderMarkdown(localText.value));

// route-derived accountId fallback (kept so PowerAgents keeps working)
const resolvedAccountId = computed(
  () => props.accountId ?? route.params.accountId
);

// ── Lifecycle ─────────────────────────────────────────────────
onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
  nextTick(() => {
    textareaRef.value?.focus();
  });
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <component
    :is="inline ? 'div' : 'Teleport'"
    v-bind="inline ? {} : { to: 'body' }"
  >
    <div
      class="prompt-editor-overlay"
      :class="inline ? 'relative' : ''"
      :style="inline ? '' : 'z-index: 9999 !important'"
      :data-account-id="resolvedAccountId"
    >
      <!-- Backdrop -->
      <div
        v-if="!inline"
        class="fixed inset-0 bg-black/70"
        style="z-index: 9998 !important"
        @click="close"
      />

      <!-- Editor panel (full width, no side panel) -->
      <div
        class="flex"
        :class="inline ? 'relative w-full h-[70vh]' : 'fixed inset-0'"
        :style="inline ? '' : 'z-index: 9999 !important'"
      >
        <!-- Main editor area -->
        <div
          class="flex-1 flex flex-col bg-[#1e1e1e] transition-all duration-200"
        >
          <!-- Toolbar. No celular os botões ficam só com o ícone (o nome segue para o leitor de
               tela, em `sr-only`) e a barra quebra linha em vez de empurrar o editor para fora. -->
          <div
            class="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-5 py-2.5 border-b border-[#2d2d2d] select-none"
          >
            <div class="flex items-center min-w-0 gap-3">
              <button
                v-if="!inline"
                class="text-[#6b6b6b] hover:text-[#d4d4d4] transition-colors p-1"
                :title="tp('EDITOR_CLOSE')"
                @click="close"
              >
                <i class="i-lucide-x text-lg" />
              </button>
              <span class="text-sm font-medium truncate text-[#d4d4d4]">{{
                agentName || agentId
              }}</span>
              <span
                class="hidden sm:inline text-[11px] text-[#5a5a5a] font-mono"
                >system prompt</span
              >
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <button
                v-if="inline"
                class="flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md border border-[#3d3d3d] text-[#a0a0a0] hover:border-[#5a5a5a] hover:text-[#d4d4d4] transition-colors"
                :title="tp('EDITOR_EXPAND')"
                @click="emit('expand')"
              >
                <i class="i-lucide-maximize-2 text-sm" />
                <span class="sr-only sm:not-sr-only">{{
                  tp('EDITOR_EXPAND')
                }}</span>
              </button>

              <button
                class="flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md border transition-colors"
                :class="
                  previewMode
                    ? 'border-[#ff13f0] bg-[#ff13f0]/10 text-[#ff13f0]'
                    : 'border-[#3d3d3d] text-[#a0a0a0] hover:border-[#5a5a5a] hover:text-[#d4d4d4]'
                "
                @click="previewMode = !previewMode"
              >
                <i
                  :class="previewMode ? 'i-lucide-pen-line' : 'i-lucide-eye'"
                  class="text-sm"
                />
                <span class="sr-only sm:not-sr-only">{{
                  previewMode ? 'Edit' : 'Preview'
                }}</span>
              </button>

              <button
                class="flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md border transition-colors"
                :class="
                  showVersions
                    ? 'border-[#ff13f0] bg-[#ff13f0]/10 text-[#ff99f8]'
                    : 'border-[#3d3d3d] text-[#a0a0a0] hover:border-[#5a5a5a] hover:text-[#d4d4d4]'
                "
                @click="showVersions = !showVersions"
              >
                <i class="i-lucide-git-branch text-sm" />
                <span class="sr-only sm:not-sr-only">Versions</span>
              </button>

              <button
                class="flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md border border-[#3d3d3d] text-[#a0a0a0] hover:border-[#5a5a5a] hover:text-[#d4d4d4] transition-colors"
                @click="showSaveDialog = true"
              >
                <i class="i-lucide-git-commit text-sm" />
                <span class="sr-only sm:not-sr-only">{{
                  tp('EDITOR_SAVE_VERSION')
                }}</span>
              </button>

              <button
                v-if="!inline"
                class="flex items-center gap-1.5 text-xs py-1.5 px-3 rounded-md bg-[#ff13f0] text-white hover:bg-[#e000d1] transition-colors"
                @click="close"
              >
                <i class="i-lucide-check text-sm" />
                <span>Done</span>
              </button>
            </div>
          </div>

          <!-- Editor area -->
          <div class="flex-1 flex overflow-hidden">
            <!-- Edit mode -->
            <template v-if="!previewMode">
              <!-- Line numbers -->
              <div
                class="w-12 flex-shrink-0 bg-[#1a1a1a] border-r border-[#252525] py-4 select-none overflow-hidden"
              >
                <div
                  class="text-right pr-3 text-sm text-[#4a4a4a]"
                  style="
                    font-family: 'JetBrains Mono', 'Fira Code', 'Malgun Gothic',
                      'Apple SD Gothic Neo', 'Noto Sans Mono CJK KR', monospace;
                    line-height: 1.7;
                  "
                >
                  <div v-for="n in lineCount" :key="n">{{ n }}</div>
                </div>
              </div>

              <!-- Solid textarea -->
              <textarea
                ref="textareaRef"
                v-model="localText"
                class="w-full min-h-full py-4 px-4 text-sm resize-none outline-none border-0 bg-transparent"
                style="
                  font-family: 'JetBrains Mono', 'Fira Code', 'Malgun Gothic',
                    'Apple SD Gothic Neo', 'Noto Sans Mono CJK KR', monospace;
                  line-height: 1.7;
                  color: #d4d4d4;
                  caret-color: #ff13f0;
                "
                spellcheck="false"
                @input="savedIndicator = false"
              />
            </template>

            <!-- Preview mode: rendered markdown -->
            <div v-else class="flex-1 overflow-auto p-6">
              <div
                class="max-w-2xl mx-auto text-sm leading-relaxed"
                v-html="markdownHtml"
              />
            </div>
          </div>

          <!-- Status bar -->
          <div
            class="flex items-center justify-between px-5 py-1.5 border-t border-[#2d2d2d] text-[11px] font-mono select-none"
          >
            <div class="flex items-center gap-4">
              <span class="text-[#5a5a5a]">
                {{ lineCount }} lines ·
                {{ tp('EDITOR_CHARS', { n: charCount }) }}
              </span>
              <span class="text-[#4a4a4a]"
                >temp: {{ temperature.toFixed(1) }}</span
              >
            </div>
            <div class="flex items-center gap-1.5">
              <span
                class="w-1.5 h-1.5 rounded-full"
                :class="savedIndicator ? 'bg-[#4ade80]' : 'bg-[#f59e0b]'"
              />
              <span class="text-[#6b6b6b]">
                {{ savedIndicator ? tp('EDITOR_SAVED') : tp('EDITOR_UNSAVED') }}
              </span>
            </div>
          </div>
        </div>

        <!-- Version History popup (centered modal) -->
        <Transition name="fade">
          <div
            v-if="showVersions"
            class="fixed inset-0 flex items-center justify-center"
            style="z-index: 10000 !important"
          >
            <div
              class="absolute inset-0 bg-black/40"
              @click="showVersions = false"
            />
            <div
              class="relative bg-[#1e1e1e] border border-[#2d2d2d] rounded-lg w-[480px] max-w-[calc(100vw-2rem)] max-h-[70vh] flex flex-col shadow-2xl"
            >
              <PromptVersionHistory
                :key="showVersions"
                :agent-id="agentId"
                :api-base-path="apiBasePath"
                :i18n-prefix="i18nPrefix"
                :account-id="accountId"
                @restore="onVersionRestored"
                @close="showVersions = false"
              />
            </div>
          </div>
        </Transition>
      </div>

      <!-- Save Version Dialog -->
      <Transition name="fade">
        <div
          v-if="showSaveDialog"
          class="fixed inset-0 flex items-center justify-center"
          style="z-index: 10000 !important"
        >
          <div
            class="absolute inset-0 bg-black/40"
            @click="showSaveDialog = false"
          />
          <div
            class="relative bg-[#252525] border border-[#3d3d3d] rounded-lg p-5 w-96 max-w-[calc(100vw-2rem)] shadow-2xl"
          >
            <h4 class="text-sm font-medium text-[#d4d4d4] mb-1">
              {{ tp('EDITOR_SAVE_VERSION') }}
            </h4>
            <p class="text-xs text-[#6b6b6b] mb-3">
              {{ tp('EDITOR_SAVE_VERSION_DESC') }}
            </p>
            <input
              v-model="versionDescription"
              class="w-full px-3 py-2 rounded-md bg-[#1e1e1e] border border-[#3d3d3d] text-sm text-[#d4d4d4] placeholder-[#5a5a5a] outline-none focus:border-[#ff13f0] transition-colors"
              placeholder="tom mais formal, adicionada seção de preços..."
              autofocus
              @keydown.enter="saveVersion()"
              @keydown.escape="showSaveDialog = false"
            />
            <div class="flex gap-2 justify-end mt-4">
              <button
                class="text-xs py-1.5 px-3 rounded-md text-[#a0a0a0] hover:text-[#d4d4d4] transition-colors"
                @click="showSaveDialog = false"
              >
                {{ tp('CANCEL') }}
              </button>
              <button
                class="text-xs py-1.5 px-3 rounded-md bg-[#ff13f0] text-white hover:bg-[#e000d1] transition-colors disabled:opacity-50"
                :disabled="!versionDescription.trim() || savingVersion"
                @click="saveVersion()"
              >
                {{ savingVersion ? '...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </component>
</template>

<style scoped>
/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Scrollbar */
textarea::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
textarea::-webkit-scrollbar-track {
  background: transparent;
}
textarea::-webkit-scrollbar-thumb {
  background: #3d3d3d;
  border-radius: 3px;
}
textarea::-webkit-scrollbar-thumb:hover {
  background: #5a5a5a;
}

/* Markdown preview */
:deep(.md-h2) {
  font-size: 1.25rem;
  font-weight: 600;
  color: #d4d4d4;
  margin: 1.25em 0 0.5em;
  border-bottom: 1px solid #2d2d2d;
  padding-bottom: 0.3em;
}
:deep(.md-h3) {
  font-size: 1.1rem;
  font-weight: 600;
  color: #d4d4d4;
  margin: 1em 0 0.4em;
}
:deep(.md-h4) {
  font-size: 1rem;
  font-weight: 600;
  color: #d4d4d4;
  margin: 0.8em 0 0.3em;
}
:deep(.md-p) {
  color: #a0a0a0;
  margin: 0.5em 0;
}
:deep(.md-li) {
  color: #a0a0a0;
  margin: 0.15em 0;
  padding-left: 1em;
}
:deep(.md-code) {
  background: #252525;
  color: #ff99f8;
  padding: 0.15em 0.4em;
  border-radius: 3px;
  font-size: 0.9em;
  font-family: inherit;
}
:deep(.md-table) {
  border-collapse: collapse;
  margin: 0.8em 0;
  width: 100%;
  /* Tabela larga rola dentro de si, e não empurra o editor inteiro para o lado. */
  display: block;
  overflow-x: auto;
}
:deep(.md-th) {
  color: #d4d4d4;
  font-weight: 600;
  text-align: left;
  padding: 0.35em 0.7em;
  border-bottom: 1px solid #3d3d3d;
  white-space: nowrap;
}
:deep(.md-td) {
  color: #a0a0a0;
  padding: 0.35em 0.7em;
  border-bottom: 1px solid #2d2d2d;
  vertical-align: top;
}
:deep(strong) {
  color: #d4d4d4;
}
:deep(em) {
  color: #c0c0c0;
}
</style>
